// SPDX-License-Identifier: Apache-2.0
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Session = exports.ErrUnitialisedClient = void 0;
const transactions_1 = require("./api/transactions");
const client_1 = require("./client");
const receipt_dispatcher_1 = require("./utils/receipt_dispatcher");
exports.ErrUnitialisedClient = new Error("client unitialised");
class Session extends client_1.Client {
    constructor(address, signer, enclave) {
        super(signer, enclave);
        this.enclaveWriter = this.enclaveConn;
        this.signer = signer;
        this.address = address;
        // Start with an invalid nonce, so that it will be queried anew
        // upon its next use.
        this.nonce = 0n;
        // When encountering any error, assume it might be a nonce mismatch. In
        // this case, reset the nonce to an invalid value.
        this.enclaveWriter.on("error", () => {
            this.nonce = 0n;
        });
        this.receiptDispatcher = new receipt_dispatcher_1.ReceiptDispatcher(this.erdstallConn);
        this.on_internal("receipt", this.receiptDispatcher.watch.bind(this.receiptDispatcher));
    }
    // Queries the next nonce and increases the counter. If the nonce has an
    // invalid value, queries the current nonce from the enclave. This function
    // can be called concurrently.
    async nextNonce() {
        if (!this.nonce) {
            await this.updateNonce();
        }
        return this.nonce++;
    }
    // Fetches the current nonce from the enclave. Only overwrites the nonce if
    // it has an invalid value, so this function can be called concurrently.
    async updateNonce() {
        const acc = await this.enclaveWriter.getAccount(this.address);
        if (!this.nonce) {
            this.nonce = acc.account.nonce + 1n;
        }
    }
    async onboard() {
        return this.enclaveWriter.onboard(this.address);
    }
    async subscribeSelf() {
        return this.subscribe(this.address);
    }
    async getOwnAccount() {
        return this.getAccount(this.address);
    }
    async transferTo(assets, to) {
        if (!this.erdstallConn) {
            throw exports.ErrUnitialisedClient;
        }
        const nonce = await this.nextNonce();
        const tx = new transactions_1.Transfer(this.address, nonce, to, assets);
        await tx.sign(this.erdstallConn.erdstall(), this.signer);
        const hash = tx.hash();
        const receipt = this.receiptDispatcher.register(hash);
        const accepted = this.enclaveWriter.transfer(tx);
        return { receipt, accepted };
    }
    async mint(token, id) {
        if (!this.erdstallConn) {
            throw exports.ErrUnitialisedClient;
        }
        const nonce = await this.nextNonce();
        const tx = new transactions_1.Mint(this.address, nonce, token, id);
        await tx.sign(this.erdstallConn.erdstall(), this.signer);
        const hash = tx.hash();
        const receipt = this.receiptDispatcher.register(hash);
        const accepted = this.enclaveWriter.mint(tx);
        return { receipt, accepted };
    }
    async burn(assets) {
        if (!this.erdstallConn) {
            throw exports.ErrUnitialisedClient;
        }
        const nonce = await this.nextNonce();
        const tx = new transactions_1.Burn(this.address, nonce, assets);
        await tx.sign(this.erdstallConn.erdstall(), this.signer);
        const hash = tx.hash();
        const receipt = this.receiptDispatcher.register(hash);
        const accepted = this.enclaveWriter.burn(tx);
        return { receipt, accepted };
    }
    async deposit(assets) {
        if (!this.erdstallConn) {
            return Promise.reject(exports.ErrUnitialisedClient);
        }
        return this.erdstallConn.deposit(assets);
    }
    async exit() {
        if (!this.erdstallConn) {
            return Promise.reject(exports.ErrUnitialisedClient);
        }
        const exittx = new transactions_1.ExitRequest(this.address, await this.nextNonce());
        await exittx.sign(this.erdstallConn.erdstall(), this.signer);
        return this.enclaveWriter.exit(exittx);
    }
    async withdraw(exitProof) {
        if (!this.erdstallConn) {
            return Promise.reject(exports.ErrUnitialisedClient);
        }
        return this.erdstallConn.withdraw(exitProof);
    }
    async leave(notify) {
        let skipped = 0;
        let cb;
        let atStage = 1;
        let maxStages = 3;
        const p = new Promise((accept) => {
            cb = () => {
                // One Epoch when the current epoch ends for which we receive the ExitProof.
                // One more Epoch to guarantee that our ExitProof is part of a sealed epoch.
                if (skipped < 2) {
                    skipped++;
                }
                else {
                    accept();
                }
            };
            this.on_internal("phaseshift", cb);
        });
        notify?.("awaiting exit proof", atStage++, maxStages);
        const exitProof = await this.exit();
        notify?.("awaiting epoch sealing", atStage++, maxStages);
        await p.then(() => this.off_internal("phaseshift", cb));
        notify?.("withdrawing", atStage++, maxStages);
        return this.withdraw(exitProof);
    }
    async createOffer(offer, expect) {
        const o = new transactions_1.TradeOffer(this.address, offer, expect);
        return o.sign(this.erdstallConn.erdstall(), this.signer);
    }
    async acceptTrade(offer) {
        if (!this.erdstallConn) {
            throw exports.ErrUnitialisedClient;
        }
        const nonce = await this.nextNonce();
        const tx = new transactions_1.Trade(this.address, nonce, offer);
        await tx.sign(this.erdstallConn.erdstall(), this.signer);
        const hash = tx.hash();
        const receipt = this.receiptDispatcher.register(hash);
        const accepted = this.enclaveWriter.trade(tx);
        return { receipt, accepted };
    }
}
exports.Session = Session;
//# sourceMappingURL=session.js.map